import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {Button, Popconfirm, Tag, Typography} from 'antd';
import {dateFormat} from 'helpers/formats';
import {observer} from 'mobx-react-lite';
import {CarEquipmentDto, GarageSpotEquipmentStatus} from '../../../../../../PRMGApiClient';
import {EquipmentEditRow, GarageSpotEquipmentStatus2LabelMapping} from './EquipmentEditRow';
import {useTranslation} from 'react-i18next';

const {Text} = Typography;

interface EquipmentRowProps {
  rowData: CarEquipmentDto;
  onDelete?: (id: number) => void;
  onSave?: (payload: CarEquipmentDto) => Promise<void>;
}

export const EquipmentRow: React.FunctionComponent<EquipmentRowProps> = observer(
  ({rowData, onDelete, onSave}): JSX.Element => {
    const {t} = useTranslation();
    const [inEditMode, setInEditMode] = useState(false);

    const form = useForm<CarEquipmentDto>({
      defaultValues: rowData,
    });
    const {handleSubmit, reset} = form;

    return inEditMode && onSave ? (
      <EquipmentEditRow
        form={form}
        onConfirm={handleSubmit((payload: CarEquipmentDto) => {
          onSave(payload).then(() => {
            setInEditMode(false);
            reset(payload);
          });
        })}
        onCancel={() => setInEditMode(false)}
      />
    ) : (
      <tr className="text-black break-words">
        <td>
          <Text>{rowData.name}</Text>
        </td>
        <td>
          <Text>{rowData.quantity}</Text>
        </td>
        <td>
          <Tag color="blue" className="text-center">
            {rowData.status &&
              GarageSpotEquipmentStatus2LabelMapping[rowData.status as keyof typeof GarageSpotEquipmentStatus]}
          </Tag>
        </td>
        <td>
          <Text>{rowData.description}</Text>
        </td>
        <td>
          <Text>{rowData.location}</Text>
        </td>
        <td>
          <Text>{dateFormat(rowData.addedDate)}</Text>
        </td>
        {onDelete && (
          <td>
            <div className="flex">
              <Button type="link" icon={<EditOutlined />} onClick={() => setInEditMode(true)} />
              <Popconfirm
                title={t('Are you sure you want to remove?')}
                onConfirm={() => rowData.id && onDelete(rowData.id)}
              >
                <Button type="link" icon={<DeleteOutlined />} />
              </Popconfirm>
            </div>
          </td>
        )}
      </tr>
    );
  }
);
